import { useQuery } from '@tanstack/vue-query'
import type { UndefinedInitialQueryOptions } from '@tanstack/vue-query'
import { getListingFilters } from '@shopware/helpers'
import type { Schemas } from '#shopware'

export type useQueryListingOptions = {
  limit: number
}

type AggregationList = {
  name: string
  apiAlias: string
  entities: {
    id: string
    name: string
    translated: { name: string }
    options: {
      id: string
      name: string
    }[]
  }[]
}

/**
 * incomplete type definition
 */
export type EntityListingResult<T> = Modify<Schemas['EntitySearchResult'], {
  elements: T[]
  aggregations: {
    manufacturer: AggregationList
    properties: AggregationList
  }
  manufacturer: AggregationList
  availableSortings: {
    key: string
    label: string
    translated: { label: string }
  }[]
}>

export const usePaginatedListing = <T>(
  paginator: (page: Ref<number>, criteria: Ref<Schemas['ProductListingCriteria']>) => UndefinedInitialQueryOptions<EntityListingResult<T>>,
  _page?: Ref<number>,
  _criteria?: Ref<Schemas['ProductListingCriteria']>,
  _enabled?: ComputedRef<boolean>,
) => {
  const page = _page ? _page : ref(1)
  const criteria = _criteria ? _criteria : ref<Schemas['ProductListingCriteria']>({})

  const queryOptions = paginator(page, criteria)

  const {
    data,
    isPending,
    isError,
    error,
    fetchStatus,
    suspense,
    isLoading,
    refetch,
  } = useQuery({
    ...queryOptions,
    enabled: _enabled !== undefined ? _enabled : true,
  })

  const total = computed(() => data.value?.total ?? 0)
  const limit = computed(() => data.value?.limit ?? 0)
  const elements = computed(() => data.value?.elements ?? [])
  const pageCount = computed(() => Math.ceil(total.value / limit.value) || 1)
  const sortingOptions = computed(() => data.value?.availableSortings ?? [])
  const filterOptions = computed(() => data.value?.aggregations)
  const manufacturer = computed(() => data.value?.manufacturer ?? [])
  const availableFilters = computed(() => data.value?.aggregations ? getListingFilters(data.value?.aggregations) : [])

  const nextPage = () => {
    page.value = Math.min(pageCount.value, +page.value + 1)
  }

  const previousPage = () => {
    page.value = Math.max(1, +page.value - 1)
  }

  return {
    elements,
    error,
    filterOptions,
    availableFilters,
    isError,
    isPending,
    nextPage,
    page,
    pageCount,
    previousPage,
    fetchStatus,
    data,
    sortingOptions,
    manufacturer,
    suspense,
    total,
    isLoading,
    refetch,
  }
}
